import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import UseCaseFormRenderer from '../simulationUseCase/UseCaseFormRenderer';
import type { UseCaseType } from '../landing/LandingScreen';
import MarketplaceShell from './MarketplaceShell';
import StepIndicator, { WIZARD_STEP_LABELS } from './StepIndicator';
import { labelForUseCase } from './useCaseMeta';
import { MktButton } from './marketplaceUi';
import { createStudy, saveStudyInputs, type StudySelection } from './marketplaceApi';
import {
  MARKETPLACE_RUN_PATH,
  MARKETPLACE_BUILD_PATH,
  audienceStepState,
} from './marketplaceLinks';
import { saveDraft, loadDraft, draftIsUsable } from './wizardDraft';
import './marketplaceForm.css';

interface FlowState {
  useCase: string;
  selection?: StudySelection;
  selections?: StudySelection[];
  /** Distinct Twin count for the selection, carried from the builder so it is
   *  never recomputed by summing per-archetype counts (which would double-count
   *  personas that belong to multiple categories). Purely informational here:
   *  createStudy re-resolves + de-dupes server-side and returns the billed count. */
  twinCount?: number;
  studyId?: number;
  /** A pre-payment study this pass abandoned (audience/use case changed on a resumed
   *  study). Passed to createStudy so the server retires it and expires its Stripe
   *  checkout, instead of leaving a ghost "Awaiting payment" row that is still payable. */
  supersededStudyId?: number;
  paid?: boolean;
  /** The form's editable state, stashed when advancing to review so a Back
   *  restores the form (and reuses the same draft) instead of starting over. */
  savedFormData?: Record<string, unknown>;
}

// Marketplace use case -> the platform's real use-case form (same components the
// main app uses, with real-time question parsing + image upload/parsing).
const USE_CASE_TO_FORM: Record<string, UseCaseType> = {
  survey: 'survey-and-focus-groups',
  concept: 'vitamin-concept-test',   // new concept UI (result mocked server-side for now)
  packaging: 'packaging-review-v2',
};

// Packaging cannot run without its front packaging image — enforced in the form
// AND again here at the wizard boundary (see finalize).
const PACKAGING_USE_CASE = 'packaging';
const MISSING_PACKAGING_IMAGE_ERROR =
  'A front packaging image is required to run this study. Please upload one to continue.';

/** True when a use-case payload carries a non-empty front packaging image. */
const hasFrontPackagingImage = (inputs: Record<string, unknown>): boolean => {
  const data = (inputs?.data ?? {}) as Record<string, unknown>;
  return typeof data.front_image === 'string' && data.front_image.length > 0;
};


const FillStudyScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // A hard reload drops history state entirely. Fall back to the session draft so a
  // refresh mid-wizard restores the use case, the audience AND the typed form,
  // instead of silently bouncing to step 1 and discarding everything (#73).
  const state = useMemo<FlowState>(() => {
    const nav = (location.state || {}) as FlowState;
    if (nav.useCase) return nav;
    const draft = loadDraft();
    return draftIsUsable(draft)
      ? {
          useCase: draft.useCase!,
          selections: draft.selections,
          twinCount: draft.twinCount,
          paid: draft.paid,
          studyId: draft.studyId,
          supersededStudyId: draft.supersededStudyId,
          savedFormData: draft.savedFormData,
        }
      : nav;
  }, [location.state]);
  const useCase = state.useCase;

  const selections = useMemo<StudySelection[]>(
    () => state.selections || (state.selection ? [state.selection] : []),
    [state],
  );

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chatStarted = useRef(false);
  // Latest editable form state, captured so advancing to review can stash it.
  const lastFormData = useRef<Record<string, unknown>>(state.savedFormData || {});

  // The platform form for this use case ('chat' has no form).
  const formType = useCase ? USE_CASE_TO_FORM[useCase] : undefined;

  const missingContext = !useCase || selections.length === 0;
  const invalidUseCase = !missingContext && useCase !== 'chat' && !formType;
  useEffect(() => {
    if (missingContext || invalidUseCase) navigate(MARKETPLACE_RUN_PATH, { replace: true });
  }, [missingContext, invalidUseCase, navigate]);

  // Chat has no fill form, but it still gets a Review step: create the study,
  // then hand off to ReviewScreen so the user confirms the audience (and the
  // cost, when paid) BEFORE anything is submitted or charged. Submission —
  // straight into the live chat when free, Stripe when paid — is ReviewScreen's
  // job now. Runs once on mount.
  useEffect(() => {
    if (missingContext || useCase !== 'chat' || chatStarted.current) return;
    chatStarted.current = true;
    (async () => {
      setBusy(true);
      try {
        // Reuse the study we already have (e.g. the user pressed Back from Review and
        // came through again). Creating a second one would orphan the first as a stray
        // draft — and there is no way to delete it.
        const studyId = state.studyId ?? (await createStudy('chat', selections)).study_id;
        navigate('/marketplace/run/review', { replace: true, state: { studyId, useCase } });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not start chat');
        setBusy(false);
      }
    })();
  }, [missingContext, useCase, selections, state.studyId, navigate]);

  if (missingContext) return null;

  // Clean full-screen loader while we create + persist the study (a few seconds
  // with a base64 image) — replaces the old translucent overlay band.
  if (busy) {
    return (
      <MarketplaceShell maxWidth="max-w-2xl">
        <div className="flex flex-col items-center py-24 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <h2 className="mt-4 text-[15px] font-medium text-[#111]">Setting up your study…</h2>
          <p className="mt-1 text-[13px] text-[#6b6b6b]">
            One moment while we prepare {state.paid ? 'your review' : 'your run'}.
          </p>
        </div>
      </MarketplaceShell>
    );
  }

  // Create the draft (if needed), persist the filled payload, then route:
  // free -> processing, paid -> the Review page.
  const finalize = async (inputs: Record<string, unknown>) => {
    // A packaging study MUST NOT advance without its front packaging image. The
    // form already blocks this, but guard at the wizard boundary too so a Set-up
    // step that re-mounted from partial/stale state can never slip a packaging
    // study through with no image.
    if (useCase === PACKAGING_USE_CASE && !hasFrontPackagingImage(inputs)) {
      setError(MISSING_PACKAGING_IMAGE_ERROR);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Reuse an existing draft (e.g. user came Back from review) so we don't
      // create a duplicate draft on every visit. When the builder could NOT reuse the
      // row (audience or use case changed on a resumed study) it hands over the id it
      // abandoned, so the server can retire that study and expire its checkout rather
      // than stranding it in history as a payable "Awaiting payment" ghost.
      let studyId = state.studyId;
      if (!studyId) {
        const created = await createStudy(useCase, selections, state.supersededStudyId);
        studyId = created.study_id;
      }
      await saveStudyInputs(studyId, inputs);
      // Clear supersededStudyId: it has been actioned server-side, and leaving it in
      // the draft would re-send it on a later pass — by then it may name a study the
      // user has since paid for.
      saveDraft({ studyId, supersededStudyId: undefined, savedFormData: lastFormData.current });

      // Persist the draft id + the form's editable state onto THIS history entry,
      // so a Back from review reuses the same draft and restores the form. Then
      // advance to review (free → run, paid → review & pay; it reads study.is_free).
      navigate(location.pathname, {
        replace: true,
        state: { ...state, studyId, savedFormData: lastFormData.current },
      });
      navigate('/marketplace/run/review', { state: { studyId, useCase } });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not submit study');
      setBusy(false);
    }
  };

  // Chat is handled by the effect above (auto-start → live chat). Show a loader
  // while it runs, but on failure surface an error + recovery instead of an
  // endless spinner.
  if (useCase === 'chat') {
    return (
      <MarketplaceShell maxWidth="max-w-2xl">
        <div className="flex flex-col items-center py-24 text-center" role="status" aria-live="polite">
          {error ? (
            <>
              <h2 className="text-[15px] font-medium text-[#111]">We couldn’t start your chat</h2>
              <p className="mt-2 max-w-md text-[13px] text-[#6b6b6b]">Something went wrong getting your AI Consumer Twins ready. Please try again.</p>
              <div className="mt-5 flex items-center gap-2">
                <MktButton onClick={() => window.location.reload()}>Try again</MktButton>
                <MktButton variant="secondary" onClick={() => navigate(MARKETPLACE_RUN_PATH)}>Back</MktButton>
              </div>
            </>
          ) : (
            <>
              <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
              <h2 className="mt-4 text-[15px] font-medium text-[#111]">Starting your chat…</h2>
            </>
          )}
        </div>
      </MarketplaceShell>
    );
  }

  // --- Survey / Concept / Packaging: the platform's real forms ---
  // Invalid useCase is redirected by the effect above; render nothing meanwhile.
  if (!formType) return null;

  // Back must return to the audience step WITH the study context, not pop blindly
  // with navigate(-1). navigate(-1) landed on a builder history entry that carried
  // no state, so the builder remounted with an empty `preset` and silently reset the
  // use case to the first one — sending the user forward into the WRONG form (a
  // packaging study re-mounting as a survey with no image upload, #9). Both builders
  // restore from this carried state: UseCasePicker from subCategory/archetypeId, and
  // BuildAudienceScreen from the full selections + studyId. (Same reason ReviewScreen
  // avoids navigate(-1) — both build the state via audienceStepState.)
  const backToAudienceStep = () => {
    const builderPath = state.paid ? MARKETPLACE_BUILD_PATH : MARKETPLACE_RUN_PATH;
    navigate(builderPath, {
      state: audienceStepState({
        useCase,
        selections,
        twinCount: state.twinCount,
        paid: state.paid,
        studyId: state.studyId,
      }),
    });
  };

  // saveMode (onSavePayload) bypasses the enterprise audience/segment
  // requirement — the marketplace resolves Twins server-side. initialFormData +
  // onFormDataChange let the form restore when the user comes Back from review.
  const formEl = (
    <UseCaseFormRenderer
      useCaseType={formType}
      selectedSegmentIds={[]}
      personaFilters={{}}
      marketplaceMode
      initialFormData={state.savedFormData}
      onFormDataChange={(_useCaseType, data) => {
        lastFormData.current = data;
        // Mirror into the session draft so a reload restores what was typed.
        saveDraft({ savedFormData: data });
      }}
      onBack={backToAudienceStep}
      onSavePayload={(payload) => { void finalize(payload); }}
    />
  );

  // The concept test is a self-contained, full-screen experience (its own
  // progress bar + background). It gets the whole canvas rather than being boxed
  // into the narrow step column, where it looked cramped and its background
  // clashed.
  if (useCase === 'concept') {
    return (
      <MarketplaceShell bare>
        {formEl}
        {error && <div className="mx-auto max-w-[720px] px-6 pb-8 text-[13px] text-red-600">{error}</div>}
      </MarketplaceShell>
    );
  }

  // Survey / packaging are card forms — give them a comfortably wide column.
  return (
    <MarketplaceShell maxWidth="max-w-5xl">
      <StepIndicator step={3} labels={WIZARD_STEP_LABELS} />
      <h1 className="text-[22px] font-medium text-[#111]">{labelForUseCase(useCase)}</h1>
      <p className="mt-1.5 text-[13px] text-[#6b6b6b]">
        Set up your study against the selected AI Consumer Twins. {state.paid ? 'You’ll review the cost before paying.' : ''}
      </p>

      {/* Scoped theme remaps the form's enterprise styling to the marketplace
          flat/teal look (no change to the form component or the enterprise app). */}
      <div className="mkt-form-theme mt-6">{formEl}</div>

      {error && <div className="mt-3 text-[13px] text-red-600">{error}</div>}
    </MarketplaceShell>
  );
};

export default FillStudyScreen;
